import { baseService } from '@repo/services';
import { type AutomationNodeType } from '@repo/utils';

export interface CreateTriggerNodeRequest {
  id: string;
  triggerType: string;
  additionalOptions: Record<string, any>;
}

export interface SaveConditionNodeRequest {
  additionalOptions: Record<string, any>;
  nodeSubType: string;
  nodeName: string;
  parentNodeId: string;
  executionOrder?: number;
  childNodeId?: string;
}

export interface SaveActionNodeResponse extends SaveConditionNodeRequest {}

export interface SavedNodeResponseType {
  success: boolean;
  node: AutomationNodeType;
  child_nodes: AutomationNodeType[];
}

const nodeService = baseService.injectEndpoints({
  endpoints: (build) => ({
    saveTriggerNode: build.mutation<SavedNodeResponseType, CreateTriggerNodeRequest>({
      query: ({ id, triggerType, additionalOptions }) => ({
        method: 'POST',
        url: `/v1/automations/${id}/trigger_node/${encodeURIComponent(triggerType)}/create`,
        body: {
          payload: {
            additional_options: additionalOptions,
          },
        },
      }),
    }),
    saveConditionNode: build.mutation<SavedNodeResponseType, SaveConditionNodeRequest>({
      query: ({
        additionalOptions, nodeSubType, nodeName, parentNodeId, executionOrder, childNodeId,
      }) => ({
        method: 'POST',
        url: '/v1/automations/create/condition',
        body: {
          payload: {
            additional_options: additionalOptions,
            node_sub_type: nodeSubType,
            name: nodeName,
            ...(executionOrder ? { execution_order: executionOrder } : {}),
          },
          parent_node_id: parentNodeId,
          child_node_id: childNodeId,
        },
      }),
    }),
    saveActionNode: build.mutation<SavedNodeResponseType, SaveActionNodeResponse>({
      query: ({
        additionalOptions, nodeSubType, nodeName, parentNodeId, executionOrder, childNodeId,
      }) => ({
        method: 'POST',
        url: '/v1/automations/create/action/',
        body: {
          payload: {
            additional_options: additionalOptions,
            node_sub_type: nodeSubType,
            name: nodeName,
            ...(executionOrder ? { execution_order: executionOrder } : {}),
          },
          parent_node_id: parentNodeId,
          child_node_id: childNodeId,
        },
      }),
    }),
    updateNode: build.mutation<SavedNodeResponseType, Omit<CreateTriggerNodeRequest, 'triggerType'>>({
      // id is the node's id
      query: ({ id, additionalOptions }) => ({
        method: 'PATCH',
        url: `/v1/automations/update/${id}`,
        body: {
          payload: {
            additional_options: additionalOptions,
          },
        },
      }),
    }),
    deleteNode: build.mutation<{ success: boolean }, { id: string }>({
      query: ({ id }) => ({
        method: 'DELETE',
        url: `/v1/automations/delete/${id}`,
      }),
    }),
  }),
});

export const {
  useSaveTriggerNodeMutation,
  useSaveConditionNodeMutation,
  useSaveActionNodeMutation,
  useUpdateNodeMutation,
  useDeleteNodeMutation,
  endpoints,
} = nodeService;

export default nodeService;
